import {
  Badge,
  Button,
  Card,
  CardContent,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  StatPill,
  toast,
  type DataTableColumn,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Flame, Plus, Trash2, Upload, Zap } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchMe } from '../../../api/auth.js';
import {
  bulkCreateUnits,
  createUnit,
  deleteUnit,
  type UnitRow,
} from '../../../api/buildings.js';
import {
  LEAD_STATUS_LABELS,
  LEAD_STATUS_TONE,
  isLeadStatus,
} from '../units/lead-status.js';

import { ImportUnitsDialog } from './import-units-dialog.js';

interface Props {
  buildingId: string;
  units: UnitRow[];
  isLoading?: boolean;
}

const OCCUPANCY_TONE: Record<string, 'neutral' | 'success' | 'warning' | 'danger'> = {
  occupied: 'success',
  vacant: 'neutral',
  out_of_service: 'danger',
  unknown: 'neutral',
};

function occupancyLabel(s: string): string {
  return s.replace('_', ' ');
}

/** Tiny icon row showing presence/absence of gas + electricity. Three
 *  states per icon: present (colored), absent (red strikethrough),
 *  unknown (muted). Uses tooltip-friendly title attributes. */
function UtilityIcons({ unit }: { unit: UnitRow }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <UtilityIcon
        Icon={Flame}
        value={unit.gasService}
        title={
          unit.gasService === true
            ? 'Gas service: yes'
            : unit.gasService === false
              ? 'Gas service: no'
              : 'Gas service: unknown'
        }
        present="text-warning"
      />
      <UtilityIcon
        Icon={Zap}
        value={unit.electricService}
        title={
          unit.electricService === true
            ? 'Electric service: yes'
            : unit.electricService === false
              ? 'Electric service: no'
              : 'Electric service: unknown'
        }
        present="text-info"
      />
    </span>
  );
}

function UtilityIcon({
  Icon,
  value,
  title,
  present,
}: {
  Icon: typeof Flame;
  value: boolean | null;
  title: string;
  present: string;
}) {
  if (value === true)
    return (
      <span title={title} className="inline-flex">
        <Icon className={`h-3.5 w-3.5 ${present}`} aria-label={title} />
      </span>
    );
  if (value === false)
    return (
      <span title={title} aria-label={title} className="relative inline-flex">
        <Icon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
        <span
          aria-hidden
          className="absolute inset-0 flex items-center justify-center text-danger"
        >
          ⁄
        </span>
      </span>
    );
  return (
    <span title={title} className="inline-flex">
      <Icon className="h-3.5 w-3.5 text-fg-muted/40" aria-label={title} />
    </span>
  );
}

export function BuildingUnitsTable({ buildingId, units, isLoading }: Props) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const isAdmin = meQuery.data?.user.role === 'admin';
  const canImport =
    meQuery.data?.user.role === 'admin' || meQuery.data?.user.role === 'building_manager';

  const deleteMutation = useMutation({
    mutationFn: (unitId: string) => deleteUnit(unitId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['building', buildingId, 'units'] });
      void queryClient.invalidateQueries({ queryKey: ['building', buildingId] });
    },
  });

  const columns: DataTableColumn<UnitRow>[] = [
    {
      id: 'unit',
      header: 'Unit',
      description: 'The unit, room, or suite number inside this building.',
      className: 'w-24 font-medium',
      cell: (row) => row.unitNumber,
    },
    {
      id: 'floor',
      header: 'Floor',
      description: 'The floor this unit is on, when it has been recorded.',
      className: 'w-16 text-fg-secondary',
      cell: (row) => row.floor ?? '—',
    },
    {
      id: 'type',
      header: 'Type',
      description: 'What kind of space this is, such as an apartment, room, or common area.',
      cell: (row) =>
        row.unitType ? (
          <span className="capitalize text-fg-secondary">{row.unitType.replace('_', ' ')}</span>
        ) : (
          <span className="text-fg-muted">—</span>
        ),
    },
    {
      id: 'occupancy',
      header: 'Occupancy',
      description: 'Whether the unit is occupied, vacant, out of service, or not yet recorded.',
      cell: (row) => (
        <Badge
          variant={
            (OCCUPANCY_TONE[row.occupancyStatus] as 'success' | 'warning' | 'danger') ?? 'default'
          }
          className="capitalize"
        >
          {occupancyLabel(row.occupancyStatus)}
        </Badge>
      ),
    },
    {
      id: 'br',
      header: 'BR',
      description: 'Number of bedrooms.',
      className: 'w-12 tabular-nums text-fg-secondary',
      cell: (row) => row.bedroomCount ?? '—',
    },
    {
      id: 'utilities',
      header: 'Utilities',
      description:
        'Gas (flame) and electric (bolt). A solid icon means that service is on, a slash means it is off, and a faded icon means it has not been recorded.',
      className: 'w-24',
      cell: (row) => <UtilityIcons unit={row} />,
    },
    {
      id: 'lead',
      header: 'Lead',
      description: 'Lead-paint status rolled up from tests, exemptions, and work.',
      className: 'w-24',
      cell: (row) => {
        const lead = isLeadStatus(row.leadStatus) ? row.leadStatus : null;
        if (!lead) return <span className="text-fg-muted">—</span>;
        return (
          <StatPill
            tone={LEAD_STATUS_TONE[lead]}
            value={LEAD_STATUS_LABELS[lead]}
            withDot
          />
        );
      },
    },
    {
      id: 'wos',
      header: 'Open WOs',
      description: 'Work orders on this unit that are still open.',
      className: 'w-24 tabular-nums',
      cell: (row) => {
        const count = row.openWorkOrderCount ?? 0;
        return (
          <StatPill
            tone={count > 0 ? 'warning' : 'neutral'}
            value={count}
          />
        );
      },
    },
  ];
  if (isAdmin) {
    columns.push({
      id: 'actions',
      header: '',
      className: 'w-16 text-right',
      cell: (row) => (
        <Button
          variant="ghost"
          size="icon"
          aria-label="Archive unit"
          onClick={(e) => {
            e.stopPropagation();
            if (confirm(`Archive unit ${row.unitNumber}?`)) {
              deleteMutation.mutate(row.id);
            }
          }}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ),
    });
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">Units</h3>
            <p className="text-xs text-fg-muted">{units.length} active</p>
          </div>
          <div className="flex items-center gap-2">
            {canImport ? (
              <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
                <Upload className="h-4 w-4" /> Import CSV
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)}>
              Bulk add
            </Button>
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4" /> Add unit
            </Button>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={units}
          rowKey={(r) => r.id}
          isLoading={isLoading}
          empty="No units yet. Use Bulk add for a range like 101-120."
          onRowClick={(row) => navigate(`/units/${row.id}`)}
        />
      </CardContent>

      <AddUnitDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        buildingId={buildingId}
      />
      <BulkAddUnitsDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        buildingId={buildingId}
      />
      <ImportUnitsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        buildingId={buildingId}
      />
    </Card>
  );
}

function AddUnitDialog({
  open,
  onOpenChange,
  buildingId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  buildingId: string;
}) {
  const queryClient = useQueryClient();
  const unitNumberId = useId();
  const floorId = useId();
  const [unitNumber, setUnitNumber] = useState('');
  const [floor, setFloor] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      createUnit(buildingId, {
        unitNumber,
        floor: floor || null,
        unitType: null,
        bedroomCount: null,
        bathroomCount: null,
        squareFeet: null,
        notes: null,
        metadata: {},
        occupancyStatus: 'unknown',
        // Migration 1: utilities + appliance summary default to "unknown"
        // (null) on creation. Property managers fill these in once the
        // unit is on the radar; the unit detail page lets them edit
        // inline.
        gasService: null,
        electricService: null,
        heatSource: null,
        hotWaterSource: null,
        stoveFuel: null,
        stoveLastReplaced: null,
        refrigeratorLastReplaced: null,
        dishwasherPresent: null,
        dishwasherLastReplaced: null,
        washerDryerPresent: null,
        washerDryerLastReplaced: null,
        windowAcCount: null,
        centralAc: null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['building', buildingId, 'units'] });
      void queryClient.invalidateQueries({ queryKey: ['building', buildingId] });
      toast.success(`Unit ${unitNumber} added.`);
      setUnitNumber('');
      setFloor('');
      onOpenChange(false);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to add unit'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!unitNumber.trim()) {
      setError('Unit number is required');
      return;
    }
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add a unit</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={unitNumberId}>Unit number</Label>
            <Input
              id={unitNumberId}
              value={unitNumber}
              onChange={(e) => setUnitNumber(e.target.value)}
              autoFocus
              placeholder="e.g. 4B"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={floorId}>Floor (optional)</Label>
            <Input
              id={floorId}
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
              placeholder="4"
            />
          </div>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Adding…' : 'Add unit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BulkAddUnitsDialog({
  open,
  onOpenChange,
  buildingId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  buildingId: string;
}) {
  const queryClient = useQueryClient();
  const fromId = useId();
  const toId = useId();
  const floorId = useId();
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [floor, setFloor] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      bulkCreateUnits(buildingId, {
        mode: 'range',
        rangeStart,
        rangeEnd,
        floor: floor || null,
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['building', buildingId, 'units'] });
      void queryClient.invalidateQueries({ queryKey: ['building', buildingId] });
      setRangeStart('');
      setRangeEnd('');
      setFloor('');
      onOpenChange(false);
      const message =
        result.skipped > 0
          ? `${result.inserted} added, ${result.skipped} skipped (duplicates).`
          : `${result.inserted} unit${result.inserted === 1 ? '' : 's'} added.`;
      toast.success(message);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to add units'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!rangeStart || !rangeEnd) {
      setError('Start and end are required');
      return;
    }
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Bulk add units</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={fromId}>From</Label>
              <Input
                id={fromId}
                value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)}
                placeholder="101"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={toId}>To</Label>
              <Input
                id={toId}
                value={rangeEnd}
                onChange={(e) => setRangeEnd(e.target.value)}
                placeholder="120"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={floorId}>Floor (optional)</Label>
            <Input id={floorId} value={floor} onChange={(e) => setFloor(e.target.value)} />
          </div>
          <p className="text-xs text-fg-muted">
            Numeric ranges (101-120) and prefixed ranges (A1-A10) are both supported. Existing
            duplicates are skipped automatically.
          </p>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Adding…' : 'Add units'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
